import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import {
  conflictosAbiertos,
  elDia,
  solicitudesPendientes,
  tieneProximaCita,
  type ColumnaHoy,
  type FilaHoy,
} from '../datos/consultas.js'
import { encolar, marcarLlegada } from '../datos/sync.js'
import { db } from '../datos/db.js'
import { fechaLarga, guaranies, hora, hoy as diaDeHoy, sumarDias } from '../datos/formato.js'
import { TituloPagina } from '../comunes/AppShell.js'
import { Boton } from '../comunes/Boton.js'
import { Bandeja } from './Bandeja.js'

/**
 * El día de la clínica (T24b).
 *
 * **Por qué no alcanza la grilla.** La agenda contesta «¿qué hueco hay el
 * jueves?», que es la pregunta de quien agenda. Esta contesta la otra, la que
 * el mostrador tiene abierta las ocho horas: «¿en qué estado está cada persona
 * que vino hoy?». En una grilla eso hay que deducirlo mirando bloques de
 * colores y abriendo cada uno.
 *
 * Es la adaptación del routing panel que usan los sistemas dentales: la lista
 * donde el equipo clínico y el mostrador se encuentran. El profesional marca
 * que sentó al paciente; la recepción ve aparecer «para cobrar» sin que nadie
 * le avise.
 *
 * **El cobro no es un lugar al que ir: es una tarea de la salida.** Por eso
 * cuelga de la fila del paciente que se está por ir, y no sólo de la pantalla
 * de Dinero.
 *
 * Lee sólo de Dexie. Si a las ocho de la mañana no hay internet, la sala se
 * llena igual.
 */

const SECCIONES: { id: ColumnaHoy; titulo: string; tono: string }[] = [
  { id: 'esperando', titulo: 'Esperando', tono: 'text-amber-800' },
  { id: 'en_sillon', titulo: 'En sillón', tono: 'text-teal-800' },
  { id: 'para_cobrar', titulo: 'Para cobrar', tono: 'text-amber-800' },
  { id: 'agendada', titulo: 'Agendadas', tono: 'text-slate-600' },
]

export interface HoyProps {
  dia: string
  onDia: (dia: string) => void
  onAtender: (citaId: string) => void
  onCobrar: (citaId: string) => void
  onVerPaciente: (clienteId: string) => void
  onAbrirCita: (citaId: string) => void
}

export function Hoy({ dia, onDia, onAtender, onCobrar, onVerPaciente, onAbrirCita }: HoyProps) {
  const [verBandeja, setVerBandeja] = useState(false)
  const [verCerradas, setVerCerradas] = useState(false)

  const filas = useLiveQuery(() => elDia(dia), [dia], [])
  const solicitudes = useLiveQuery(() => solicitudesPendientes(), [], [])
  const conflictos = useLiveQuery(() => conflictosAbiertos(), [], [])

  const cerradas = filas.filter((f) => f.columna === 'cerrada')
  const pendientes = solicitudes.length + conflictos.length

  return (
    <div>
      <TituloPagina
        titulo="Hoy"
        bajada={fechaLarga(`${dia}T12:00:00Z`)}
        acciones={
          <>
            {dia !== diaDeHoy() && (
              <Boton variante="secondary" onClick={() => onDia(diaDeHoy())}>
                Hoy
              </Boton>
            )}
            <div className="flex items-center gap-1">
              <Boton
                variante="ghost"
                onClick={() => onDia(sumarDias(dia, -1))}
                aria-label="Día anterior"
                className="w-9 px-0 text-lg"
              >
                ‹
              </Boton>
              <Boton
                variante="ghost"
                onClick={() => onDia(sumarDias(dia, 1))}
                aria-label="Día siguiente"
                className="w-9 px-0 text-lg"
              >
                ›
              </Boton>
            </div>
          </>
        }
      />

      {/* La bandeja dejó de ser pestaña: una pestaña que el 90 % de los días
          está vacía enseña a ignorarla. Acá aparece sólo cuando hay algo. */}
      {pendientes > 0 && (
        <div className="mb-5 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3">
          <div className="flex flex-wrap items-center gap-3 text-sm text-sky-900">
            <span className="min-w-0 flex-1">
              {solicitudes.length > 0 && (
                <strong className="font-medium">
                  {solicitudes.length} {solicitudes.length === 1 ? 'solicitud' : 'solicitudes'} de la web
                </strong>
              )}
              {solicitudes.length > 0 && conflictos.length > 0 && ' · '}
              {conflictos.length > 0 && (
                <strong className="font-medium">
                  {conflictos.length} {conflictos.length === 1 ? 'escritura' : 'escrituras'} sin lugar
                </strong>
              )}
            </span>
            <Boton variante="secondary" tamano="sm" onClick={() => setVerBandeja(!verBandeja)}>
              {verBandeja ? 'Ocultar' : 'Ver'}
            </Boton>
          </div>
          {verBandeja && (
            <div className="mt-4 border-t border-sky-200 pt-4">
              <Bandeja compacta />
            </div>
          )}
        </div>
      )}

      {filas.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-white py-12 text-center">
          <p className="txt-body">Todavía no hay nadie agendado para este día.</p>
        </div>
      )}

      {SECCIONES.map(({ id, titulo, tono }) => {
        const suyas = filas.filter((f) => f.columna === id)
        if (suyas.length === 0) return null
        return (
          <section key={id} className="mb-6">
            <h2 className={`mb-2 text-[13px] font-medium ${tono}`}>
              {titulo} ({suyas.length})
            </h2>
            <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
              {suyas.map((fila) => (
                <li key={fila.cita.id}>
                  <Fila
                    fila={fila}
                    onAtender={onAtender}
                    onCobrar={onCobrar}
                    onVerPaciente={onVerPaciente}
                    onAbrirCita={onAbrirCita}
                  />
                </li>
              ))}
            </ul>
          </section>
        )
      })}

      {cerradas.length > 0 && (
        <section className="mb-6">
          <button
            type="button"
            onClick={() => setVerCerradas(!verCerradas)}
            className="mb-2 text-[13px] text-slate-400 hover:text-slate-700"
          >
            {verCerradas ? '▾' : '▸'} Cerradas ({cerradas.length})
          </button>
          {verCerradas && (
            <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white opacity-70">
              {cerradas.map((fila) => (
                <li key={fila.cita.id}>
                  <Fila
                    fila={fila}
                    onAtender={onAtender}
                    onCobrar={onCobrar}
                    onVerPaciente={onVerPaciente}
                    onAbrirCita={onAbrirCita}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  )
}

/** Una persona del día, con lo único que se puede hacer con ella ahora mismo. */
function Fila({
  fila,
  onAtender,
  onCobrar,
  onVerPaciente,
  onAbrirCita,
}: {
  fila: FilaHoy
  onAtender: (citaId: string) => void
  onCobrar: (citaId: string) => void
  onVerPaciente: (clienteId: string) => void
  onAbrirCita: (citaId: string) => void
}) {
  const { cita, tramo, cliente, profesional, columna, saldo, esperandoMin } = fila

  // Las tareas de salida: lo que falta antes de que la persona se vaya. Se
  // calcula sólo para quien está por irse, que es cuando sirve.
  const proxima = useLiveQuery(
    async () => {
      if (columna !== 'para_cobrar' || !cliente) return true
      return tieneProximaCita(cliente.id)
    },
    [columna, cliente?.id],
    true,
  )

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5">
      <span className="w-12 shrink-0 text-[13px] tabular-nums text-slate-500">
        {hora(tramo.inicio)}
      </span>

      <div className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-medium text-slate-900">
          {cliente?.nombre ?? 'Sin paciente'}
        </span>
        {columna === 'para_cobrar' && (
          <span className="block text-[12px] text-amber-700">
            cobrar {guaranies(saldo)}
            {proxima === false && ' · sin próximo control'}
          </span>
        )}
      </div>

      <span className="hidden w-40 shrink-0 truncate text-[13px] text-slate-500 sm:block">
        {profesional ?? 'Sin profesional'}
      </span>

      {esperandoMin !== null && (
        <span
          className={`w-24 shrink-0 text-right text-[13px] tabular-nums ${
            // Un cuarto de hora es donde una espera deja de ser normal y pasa a
            // ser algo que alguien tiene que explicar.
            esperandoMin > 15 ? 'font-medium text-amber-700' : 'text-slate-500'
          }`}
        >
          {esperandoMin} min
        </span>
      )}

      <div className="flex shrink-0 items-center gap-2">
        {columna === 'agendada' && (
          <>
            <Boton variante="primary" tamano="sm" onClick={() => void marcarLlegada(cita.id)}>
              Llegó
            </Boton>
            <Boton
              variante="ghost"
              tamano="sm"
              onClick={async () => {
                await db.cita.update(cita.id, { estado: 'no_asistio' })
                await encolar('cambiar_estado', { citaId: cita.id, estado: 'no_asistio' })
              }}
            >
              No asistió
            </Boton>
          </>
        )}

        {columna === 'esperando' && (
          <Boton variante="primary" tamano="sm" onClick={() => onAtender(cita.id)}>
            Atender
          </Boton>
        )}

        {columna === 'en_sillon' && (
          <Boton variante="secondary" tamano="sm" onClick={() => onAtender(cita.id)}>
            Ver atención
          </Boton>
        )}

        {columna === 'para_cobrar' && (
          <Boton variante="primary" tamano="sm" onClick={() => onCobrar(cita.id)}>
            Cobrar
          </Boton>
        )}

        {cliente && (
          <Boton variante="ghost" tamano="sm" onClick={() => onVerPaciente(cliente.id)}>
            Ficha
          </Boton>
        )}

        <Boton variante="ghost" tamano="sm" onClick={() => onAbrirCita(cita.id)}>
          En la agenda
        </Boton>
      </div>
    </div>
  )
}
